import React, { FC } from 'react';
import styled from 'styled-components';
import CheckIcon from '@mui/icons-material/Check';
import WarningIcon from '@mui/icons-material/Warning';
import { StatusTone } from '../../../common/model/type/status-tone.type';
import Colours from '../../../design-system/colours';
import Variables from '../../../design-system/variables';

interface OvStatusChipProps {
  label: string;
  tone: StatusTone;
}

const getBackgroundColor = (tone: StatusTone): string => {
  switch (tone) {
    case 'initial':
      return Colours.OV_ORDER_CREATED;

    case 'success':
      return Colours.OV_LIGHT_GRAY;

    case 'progress':
      return Colours.OV_LIGHT_BLUE;

    case 'pending':
    case 'caution':
      return Colours.OV_YELLOW;

    case 'failure':
      return Colours.OV_ERROR_ACCESSIBLE;

    case 'neutral':
      return Colours.OV_BORDER_COLOR_V3;

    default: {
      const unhandledTone: never = tone;

      return unhandledTone;
    }
  }
};

const renderIcon = (tone: StatusTone) => {
  switch (tone) {
    case 'success':
      return <CheckIcon aria-hidden="true" />;

    case 'caution':
    case 'failure':
      return <WarningIcon aria-hidden="true" />;

    case 'initial':
    case 'progress':
    case 'pending':
    case 'neutral':
      return <Dot aria-hidden="true" />;

    default: {
      const unhandledTone: never = tone;

      return unhandledTone;
    }
  }
};

const OvStatusChip: FC<OvStatusChipProps> = ({ label, tone }) => (
  <Chip
    data-tone={tone}
    style={{
      backgroundColor: getBackgroundColor(tone),
      color: tone === 'failure' ? Colours.WHITE : Colours.OV_BASE,
    }}
  >
    {renderIcon(tone)}
    <span>{label}</span>
  </Chip>
);

export default OvStatusChip;

const Chip = styled.span`
  display: inline-flex;
  align-items: center;
  flex: none;
  gap: 0.3125rem;
  white-space: nowrap;
  border-radius: ${Variables.borderRadius.CLINIC_DASHBOARD_LARGE};
  padding: 0.25rem 0.5625rem;
  font-size: ${Variables.fontSizes.MEDIUM};
  line-height: 1.3;

  > span {
    flex: none;
  }

  .MuiSvgIcon-root {
    flex: none;
    width: 0.875rem;
    height: 0.875rem;
  }
`;

const Dot = styled.span`
  flex: none;
  width: 0.3125rem;
  height: 0.3125rem;
  border-radius: ${Variables.borderRadius.CIRCLE};
  background-color: currentColor;
`;
